import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import SectionLabel from "./SectionLabel";

export default function Pricing() {
  const { t, currency, locale } = useI18n();
  const [billing, setBilling] = useState("yearly"); // monthly | yearly

  const isBR = currency === "BRL";

  const proLabel =
    billing === "monthly"
      ? isBR ? t.pricing.pro.priceMonth : t.pricing.pro.priceMonthUSD
      : isBR ? t.pricing.pro.priceYear : t.pricing.pro.priceYearUSD;
  const proPeriod = billing === "monthly" ? t.pricing.pro.perMonth : t.pricing.pro.perYear;

  const proPerMonth = billing === "yearly"
    ? (isBR ? "≈ R$ 7,49/mês" : "≈ $ 2.08/mo")
    : null;

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-20 md:py-32 border-t border-line">
      <div className="container-px mx-auto max-w-7xl">
        <SectionLabel>{t.pricing.label}</SectionLabel>
        <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-display font-bold text-ink text-balance leading-[1.05]
                         text-3xl sm:text-5xl lg:text-6xl max-w-2xl">
            {t.pricing.title}
          </h2>

          {/* Toggle */}
          <div
            className="inline-flex items-center rounded-xl hairline p-1 self-start md:self-auto"
            style={{ background: "rgba(28,31,38,0.5)" }}
            data-testid="billing-toggle"
          >
            {["monthly", "yearly"].map((b) => (
              <button
                key={b}
                onClick={() => setBilling(b)}
                className={`relative px-4 py-2 rounded-lg label-tag text-[10px] transition-colors ${
                  billing === b ? "text-bg" : "text-ink/60 hover:text-ink"
                }`}
                data-testid={`billing-${b}`}
              >
                {billing === b && (
                  <motion.span
                    layoutId="billing-pill"
                    className="absolute inset-0 rounded-lg bg-primary"
                  />
                )}
                <span className="relative">
                  {b === "monthly" ? t.pricing.monthly : t.pricing.yearly}
                  {b === "yearly" && (
                    <span className={`ml-2 text-[9px] ${billing === b ? "text-bg/70" : "neon-text"}`}>
                      −42%
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* FREE */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-tactical p-7 md:p-9 relative"
            data-testid="plan-free"
          >
            <div className="flex items-baseline justify-between">
              <p className="font-display text-ink text-2xl font-bold tracking-tight">{t.pricing.free.name}</p>
              <span className="label-tag text-[10px] text-ink/50">{t.pricing.free.tag}</span>
            </div>

            <div className="mt-6 flex items-baseline gap-2">
              <span className="mono-num text-5xl text-ink">
                {isBR ? t.pricing.free.price : t.pricing.free.priceUSD}
              </span>
              <span className="label-tag">{t.pricing.free.period}</span>
            </div>

            <a href="#cta" className="btn-ghost mt-7 w-full justify-center" data-testid="cta-free">
              {t.pricing.free.cta}
            </a>

            <ul className="mt-7 space-y-2.5">
              {t.pricing.free.features.map((f, i) => {
                const isLimited = i >= 7; // last 3 are restrictions
                return (
                  <li key={i} className="flex items-start gap-3 text-[14px]">
                    {isLimited ? (
                      <X size={14} className="mt-0.5 text-ink/30 flex-shrink-0" />
                    ) : (
                      <Check size={14} className="mt-0.5 text-primary flex-shrink-0" />
                    )}
                    <span className={isLimited ? "text-ink/40 line-through" : "text-ink/80"}>{f}</span>
                  </li>
                );
              })}
            </ul>
          </motion.div>

          {/* PRO */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative card-tactical p-7 md:p-9 overflow-hidden"
            style={{
              borderColor: "rgba(0,255,136,0.4)",
              background: "linear-gradient(180deg, rgba(0,255,136,0.06), #1C1F26 60%)",
              boxShadow: "0 0 60px -20px rgba(0,255,136,0.35)",
            }}
            data-testid="plan-pro"
          >
            {/* Badge */}
            <span className="absolute top-4 right-4 mono-num text-[10px] px-2.5 py-1 rounded-md text-bg"
                  style={{ background: "#00FF88" }}>
              {t.pricing.pro.badge}
            </span>

            <div className="flex items-baseline gap-3">
              <Sparkles size={18} color="#00FF88" />
              <p className="font-display text-ink text-2xl font-bold tracking-tight">{t.pricing.pro.name}</p>
            </div>
            <span className="label-tag text-[10px] text-ink/60 mt-1 block">{t.pricing.pro.tag}</span>

            <div className="mt-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={billing + locale}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-baseline gap-2"
                >
                  <span className="mono-num text-5xl text-ink">{proLabel}</span>
                  <span className="label-tag">{proPeriod}</span>
                </motion.div>
              </AnimatePresence>
              {proPerMonth && (
                <p className="mt-2 mono-num text-[11px] neon-text">{proPerMonth} · {t.pricing.save}</p>
              )}
            </div>

            <a href="#cta" className="btn-neon mt-7 w-full" data-testid="cta-pro">
              {t.pricing.pro.cta}
            </a>

            <ul className="mt-7 space-y-2.5">
              {t.pricing.pro.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-[14px]">
                  <Check size={14} className="mt-0.5 text-primary flex-shrink-0" />
                  <span className="text-ink/90">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
